import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  IconButton,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Tooltip,
  useTheme,

} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRight from '@mui/icons-material/KeyboardDoubleArrowRight';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const City = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [showEditCityModal, setShowEditCityModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState({ id: null, name: '', state: null, country: null });
  const [countryId, setCountryId] = useState('');
  const [stateId, setStateId] = useState('');
  const [cityName, setCityName] = useState('');

  // Data arrays
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);





  // Load data on component mount
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      const fetchData = async () => {
        try {
          const accessToken = localStorage.getItem('accessToken');

          // Fetch countries
          const countriesResponse = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setCountries(countriesResponse.data);

          // Fetch states
          const statesResponse = await axios.get(`${API_BASE_URL}/api/master/states/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setStates(statesResponse.data);

          // Fetch cities after countries and states are loaded
          const citiesResponse = await axios.get(`${API_BASE_URL}/api/master/cities/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          // Map country and state IDs to names
          const citiesWithNames = citiesResponse.data.map((city) => {
            const country = countriesResponse.data.find((c) => c.id === city.country);
            const state = statesResponse.data.find((s) => s.id === city.state);
            return {
              ...city,
              country_name: country ? country.name : 'N/A',
              state_name: state ? state.name : 'N/A',
            };
          });

          setCities(citiesWithNames);
          setFilteredData(citiesWithNames);
        } catch (err) {
          setError(err.message);
          setSnackbarSeverity('error');
          setSnackbarMessage('Failed to fetch data.');
          setOpenSnackbar(true);
          if (err.response?.status === 401) {
            localStorage.removeItem('accessToken');
            navigate('/login');
          }
        } finally {
          setLoading(false);
        }
      };

      setLoading(true);
      fetchData();
    }
  }, [navigate]);

  useEffect(() => {
    const fetchStatesByCountry = async () => {
      if (!countryId) {
        setFilteredStates([]);
        setStateId('');
        setFilteredCities([]);
        return;
      }

      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API_BASE_URL}/api/master/states/?country=${countryId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        const statesByCountry = response.data.filter(
          (state) => state.country === parseInt(countryId)
        );

        setFilteredStates(statesByCountry);
        setStateId('');
        setFilteredCities([]);
      } catch (err) {
        console.error('State fetch failed', err);
      }
    };

    fetchStatesByCountry();
  }, [countryId]);

  useEffect(() => {
    const fetchCitiesByState = async () => {
      if (!stateId) {
        setFilteredCities([]);
        return;
      }

      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API_BASE_URL}/api/master/cities/?state=${stateId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        const citiesByState = response.data.filter(
          (city) => city.state === parseInt(stateId)
        );

        setFilteredCities(citiesByState);
      } catch (err) {
        console.error('City fetch failed', err);
      }
    };

    fetchCitiesByState();
  }, [stateId]);

  // Add this new useEffect to handle edit mode state selection
  useEffect(() => {
    if (showEditCityModal && selectedCity.state && filteredStates.length > 0) {
      const stateExists = filteredStates.some(state => state.id === selectedCity.state);
      if (stateExists && stateId !== selectedCity.state) {
        setStateId(selectedCity.state);
      }
    }
  }, [showEditCityModal, selectedCity.state, filteredStates, stateId]);


  // Add a new city
  const handleSave = async (e) => {
    e.preventDefault();
    if (!cityName || !countryId || !stateId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('All fields are required.');
      setOpenSnackbar(true);
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const payload = {
        name: cityName,
        state: stateId,
        country: countryId,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/master/cities/`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      // Map the response to include country and state names
      const country = countries.find((c) => c.id === countryId);
      const state = states.find((s) => s.id === stateId);
      const newCity = {
        ...response.data,
        country_name: country ? country.name : 'N/A',
        state_name: state ? state.name : 'N/A',
      };

      setCities([...cities, newCity]);
      setFilteredData([...filteredData, newCity]);
      setShowAddCityModal(false);
      setSnackbarSeverity('success');
      setSnackbarMessage('City added successfully!');
      setOpenSnackbar(true);
      setCountryId('');
      setStateId('');
      setCityName('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');

      // Check if the error response contains the name unique constraint message
      if (err.response?.data?.name && err.response.data.name[0] === "City name must be unique within this state.") {
        setSnackbarMessage('City name must be unique within this state.');
      } else {
        setSnackbarMessage('Failed to add city.');
      }

      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Update a city
  const handleUpdate = async () => {
    if (!cityName || !countryId || !stateId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('All fields are required.');
      setOpenSnackbar(true);
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const payload = {
        name: cityName,
        state: stateId,
        country: countryId,
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/master/cities/${selectedCity.id}/`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      // Map the response to include country and state names
      const country = countries.find((c) => c.id === countryId);
      const state = states.find((s) => s.id === stateId);
      const updatedCity = {
        ...response.data,
        country_name: country ? country.name : 'N/A',
        state_name: state ? state.name : 'N/A',
      };

      const updatedCities = cities.map((city) =>
        city.id === selectedCity.id ? updatedCity : city
      );
      setCities(updatedCities);
      setFilteredData(updatedCities);
      setShowEditCityModal(false);
      setSnackbarSeverity('success');
      setSnackbarMessage('City updated successfully!');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');

      // Check if the error response contains the name unique constraint message
      if (err.response?.data?.name && err.response.data.name[0] === "City name must be unique within this state.") {
        setSnackbarMessage('City name must be unique within this state.');
      } else {
        setSnackbarMessage('Failed to update city.');
      }

      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a city
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/cities/${selectedCity.id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const updatedCities = cities.filter((city) => city.id !== selectedCity.id);
      setCities(updatedCities);
      setFilteredData(updatedCities);
      setShowDeleteModal(false);
      setSnackbarSeverity('success');
      setSnackbarMessage(`${selectedCity.name} deleted successfully!`);
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete city.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };
  const handleOpenAddCityModal = () => {
    setCountryId('');
    setStateId('');
    setCityName('');
    setFilteredStates([]);
    setShowAddCityModal(true);
  };

  // Edit a city
  const handleEdit = async (city) => {
    setSelectedCity(city);
    setCityName(city.name);
    setCountryId(city.country);
    setStateId(''); // Clear first

    // Fetch states for the selected country
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/master/states/?country=${city.country}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const statesByCountry = response.data.filter(
        (state) => state.country === parseInt(city.country)
      );

      setFilteredStates(statesByCountry);
    } catch (err) {
      console.error('State fetch failed', err);
    }

    setShowEditCityModal(true);
  };

  // Show delete modal
  const handleShowDeleteModal = (id, name) => {
    setSelectedCity({ id, name });
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Reset form
  const handleReset = () => {
    setCountryId('');
    setStateId('');
    setCityName('');
    setFilteredStates([]);
  };

  // Cancel edit
  const handleCancel = () => {
    setShowEditCityModal(false);
    setCountryId('');
    setStateId('');
    setCityName('');
    setFilteredStates([]);
  };
  // Filter cities based on search term
  useEffect(() => {
    setFilteredData(
      cities.filter((city) =>
        city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.country_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.state_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, cities]);

  // Pagination
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Container>
      {/* Search and Add Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main, marginLeft: 1.5, fontSize: 24 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'grey', marginRight: 1 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0, 0, 0, 0.4)' : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            // onClick={() => setShowAddCityModal(true)}
            onClick={handleOpenAddCityModal}
            sx={{
              ml: 2,
              width: '120px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              borderRadius: '10px',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD CITY
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <Paper elevation={3} sx={{ paddingBottom: 0, maxWidth: '100%', boxSizing: 'border-box', mx: 'auto', backgroundColor: theme.palette.background.paper, borderRadius: '16px' }}>
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Country Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>State Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>City Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((city, index) => (
                    <TableRow
                      key={city.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{city.country_name || 'N/A'}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{city.state_name || 'N/A'}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{city.name}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(city)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color='error'
                          onClick={() => handleShowDeleteModal(city.id, city.name)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedCity.name}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add City Modal */}
      <Modal open={showAddCityModal} onClose={() => setShowAddCityModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add City
            </Typography>
            <IconButton
              onClick={() => {
                setShowAddCityModal(false);
                handleReset();
              }}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="country"
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}>Country</InputLabel>
              <Select
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="state" sx={{
                color: theme.palette.text.primary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}>State</InputLabel>
              <Select
                id="state"
                value={stateId}
                label="State"
                onChange={(e) => setStateId(e.target.value)}
                disabled={!countryId || filteredStates.length === 0}
              >
                {filteredStates.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="City Name"
              variant="outlined"
              fullWidth
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!countryId ? "Select Country" : !stateId ? "Select State" : !cityName ? "Enter City Name" : ""} arrow>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark }
                  }}
                  disabled={!cityName || !countryId || !stateId}
                >
                  SAVE
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit City Modal */}
      <Modal open={showEditCityModal} onClose={handleCancel} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit City
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="country" sx={{
                color: theme.palette.text.primary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}>Country</InputLabel>
              <Select
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="state" sx={{
                color: theme.palette.text.primary,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}>State</InputLabel>
              <Select
                id="state"
                value={stateId}
                label="State"
                onChange={(e) => setStateId(e.target.value)}
                disabled={!countryId || filteredStates.length === 0}
              >
                {filteredStates.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="City Name"
              variant="outlined"
              fullWidth
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!cityName && "Enter City Name"} arrow>
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark }
                  }}
                  disabled={!cityName || !countryId || !stateId}
                >
                  UPDATE
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 900,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              City Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Follow these steps to add a new city:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Click the <strong>"ADD CITY"</strong> button at the top of the page to open the Add City dialog.
              </li>
              <li>
                Select <strong>Country</strong> from the dropdown list (loaded from API).
              </li>
              <li>
                Select <strong>State</strong> from the dropdown list (loaded from API).
              </li>
              <li>
                Enter <strong>City Name</strong> in the provided text field.
              </li>
              <li>
                Before clicking <strong>"SAVE"</strong>, Ensure that all required fields are completed before proceeding.
              </li>
              <li>
                Click <strong>"SAVE"</strong> to add the city. A success notification will appear.
              </li>
              <li>
                The newly added city will be displayed in the City List table.
              </li>
              <li>
                To clear the form without saving, click <strong>"RESET"</strong>
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Country, State, and City Name are mandatory fields.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to update an existing city:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Locate the city you want to update in the City List table.
              </li>
              <li>
                Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit City modal.
              </li>
              <li>
                Update the <strong>Country</strong>, <strong>State</strong>, or <strong>City Name</strong> as required.
              </li>
              <li>
                Before clicking <strong>"UPDATE"</strong>, Verify that all fields are filled correctly.
              </li>
              <li>
                Click <strong>"UPDATE"</strong> to save the changes. A success notification will be shown.
              </li>
              <li>
                To exit without saving, click <strong>"CANCEL"</strong>
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: All fields must be filled before updating.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to delete a city:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Find the city you want to delete in the City List table.
              </li>
              <li>
                Click the <strong>Delete</strong> icon (trash) icon in the Action column.
              </li>
              <li>
                Confirm by clicking <strong>"Yes, Delete"</strong> in the confirmation dialog.
              </li>
              <li>
                The city will be removed from the list, and a success notification will appear.
              </li>
              <li>
                To cancel the action, click  <strong>"CANCEL"</strong>
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Deleted records cannot be recovered.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to search for a city:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the Search bar located at the top-left of the City List.
              </li>
              <li>
                Enter Country Name, State Name, or City Name (full or partial).
              </li>
              <li>
                The table will automatically update to show matching results.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️Note: The search is case-insensitive and applies to Country, State, and City Name.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to navigate the city list:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the pagination controls at the bottom of the table to move between pages.
              </li>
              <li>
                Click the left (<strong>&lt;&lt;</strong>) or right (<strong>&gt;&gt;</strong>) arrows to move pages.
              </li>
              <li>
                Select a specific page number to jump directly to that page.
              </li>
              <li>
                Change the number of rows displayed per page (5, 10, 20, or 30) using the dropdown menu.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Only the selected number of rows will be displayed per page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default City;